import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useJsApiLoader, GoogleMap, Polygon } from '@react-google-maps/api';
import type { GeofenceGeoJSON } from './GeofenceMapEditor';

const MAP_CONTAINER_STYLE = { width: '100%', height: 480 };
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };
const DEFAULT_ZOOM = 17;

const COORD_EPSILON = 1e-9;

function dedupeRing(ring: number[][]): number[][] {
  if (ring.length < 2) return ring;
  const out: number[][] = [ring[0]];
  for (let i = 1; i < ring.length; i++) {
    const prev = out[out.length - 1];
    const curr = ring[i];
    if (!prev || !curr) continue;
    const same =
      Math.abs(prev[0] - curr[0]) < COORD_EPSILON && Math.abs(prev[1] - curr[1]) < COORD_EPSILON;
    if (!same) out.push(curr);
  }
  return out;
}

function pathToGeoJSONRing(path: google.maps.LatLng[]): number[][] {
  let ring = path.map((ll) => [ll.lng(), ll.lat()]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([first[0], first[1]]);
  }
  ring = dedupeRing(ring);
  if (ring.length >= 2 && ring[0] && ring[ring.length - 1]) {
    const f = ring[0];
    const l = ring[ring.length - 1];
    if (Math.abs(f[0] - l[0]) >= COORD_EPSILON || Math.abs(f[1] - l[1]) >= COORD_EPSILON) {
      ring.push([f[0], f[1]]);
    }
  }
  return ring;
}

/** Convert a circle (center + radius in meters) to a GeoJSON polygon ring (approximate circle with N points). Exported for use in Settings. */
export function circleToGeoJSONRing(centerLat: number, centerLng: number, radiusMeters: number, segments = 32): number[][] {
  const ring: number[][] = [];
  const latRad = (centerLat * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLng = 111320 * Math.cos(latRad);
  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * 2 * Math.PI;
    const dLat = (radiusMeters * Math.cos(angle)) / metersPerDegLat;
    const dLng = (radiusMeters * Math.sin(angle)) / metersPerDegLng;
    ring.push([centerLng + dLng, centerLat + dLat]);
  }
  if (ring.length > 0) ring.push([ring[0][0], ring[0][1]]);
  return ring;
}

/** Get center from request area (Polygon centroid or Circle center) for gate pin display */
function getCenterFromRequestArea(area: RequestAreaShape | null | undefined): { lat: number; lng: number } | null {
  if (!area || typeof area !== 'object') return null;
  if (area.type === 'Circle' && Array.isArray(area.center) && area.center.length >= 2) {
    return { lng: Number(area.center[0]), lat: Number(area.center[1]) };
  }
  const ring = (area as GeofenceGeoJSON)?.coordinates?.[0];
  if (!ring || !Array.isArray(ring) || ring.length < 3) return null;
  let sumLat = 0, sumLng = 0, n = 0;
  for (const c of ring) {
    if (Array.isArray(c) && c.length >= 2) {
      const lng = Number(c[0]);
      const lat = Number(c[1]);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        sumLat += lat;
        sumLng += lng;
        n++;
      }
    }
  }
  if (n === 0) return null;
  return { lat: sumLat / n, lng: sumLng / n };
}

function hasRequestArea(area: RequestAreaShape | null | undefined): area is RequestAreaShape {
  if (!area || typeof area !== 'object') return false;
  if (area.type === 'Circle') return Array.isArray(area.center) && area.center.length >= 2 && typeof area.radius === 'number' && area.radius > 0;
  return !!(area as GeofenceGeoJSON).coordinates?.[0]?.length && (area as GeofenceGeoJSON).coordinates![0].length >= 3;
}

function geofenceToPath(geofence: GeofenceGeoJSON | null | undefined): { lat: number; lng: number }[] | undefined {
  const coords = geofence?.coordinates;
  if (!coords || !Array.isArray(coords)) return undefined;
  const first = coords[0];
  let ring: number[][] = [];
  if (Array.isArray(first)) {
    if (first.length >= 2 && Array.isArray(first[0])) {
      ring = first as unknown as number[][];
    } else if (first.length >= 2 && typeof first[0] === 'number') {
      ring = coords as unknown as number[][];
    }
  }
  if (ring.length < 3) return undefined;
  const path: { lat: number; lng: number }[] = ring.map((c: number[]) => {
    const a = Number(c[0]);
    const b = Number(c[1]);
    const lng = Math.abs(a) <= 180 && Math.abs(b) <= 90 ? a : Math.abs(b) <= 180 ? b : a;
    const lat = Math.abs(a) <= 180 && Math.abs(b) <= 90 ? b : Math.abs(b) <= 180 ? a : b;
    return { lat, lng };
  });
  const p0 = path[0];
  const pLast = path[path.length - 1];
  if (p0 && pLast && (p0.lat !== pLast.lat || p0.lng !== pLast.lng)) path.push({ ...p0 });
  return path;
}

export type RequestAreaShape = GeofenceGeoJSON | { type: 'Circle'; center: [number, number]; radius: number };

export interface GateMapEditorResult {
  latitude: number;
  longitude: number;
  requestArea: RequestAreaShape | null;
}

interface GateMapEditorProps {
  gateName: string;
  initialRequestArea?: RequestAreaShape | null;
  /** Map center when no request area */
  schoolCenter?: { lat: number; lng: number };
  /** School geofence boundary – shown on map so principal can see if gate area is inside/outside */
  schoolGeofence?: GeofenceGeoJSON | null;
  /** When true, map fills available height (e.g. in a full-screen dialog) */
  fullScreen?: boolean;
  onSave: (result: GateMapEditorResult) => void;
  onCancel: () => void;
}

const GateMapEditor: React.FC<GateMapEditorProps> = ({
  gateName,
  initialRequestArea,
  schoolCenter,
  schoolGeofence,
  fullScreen = false,
  onSave,
  onCancel,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const lastDrawnPolygonRef = useRef<google.maps.Polygon | null>(null);
  const existingRequestAreaPolygonRef = useRef<google.maps.Polygon | null>(null);
  const schoolGeofencePolygonRef = useRef<google.maps.Polygon | null>(null);
  const schoolGeofencePolylineRef = useRef<google.maps.Polyline | null>(null);

  const [pendingRequestArea, setPendingRequestArea] = useState<RequestAreaShape | null>(initialRequestArea ?? null);
  const [drawingManagerReady, setDrawingManagerReady] = useState(false);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded: scriptLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: ['maps', 'drawing'],
  });

  const center = useMemo(() => {
    const fromArea = getCenterFromRequestArea((pendingRequestArea || initialRequestArea) ?? null);
    return fromArea || schoolCenter || DEFAULT_CENTER;
  }, [pendingRequestArea, initialRequestArea, schoolCenter]);

  const pendingRequestPath = useMemo((): { lat: number; lng: number }[] | undefined => {
    const area = pendingRequestArea;
    if (!area) return undefined;
    if (area.type === 'Circle') {
      const ring = circleToGeoJSONRing(area.center[1], area.center[0], area.radius);
      return ring.map(([lng, lat]) => ({ lat, lng }));
    }
    const ring = (area as GeofenceGeoJSON)?.coordinates?.[0];
    if (!ring || !Array.isArray(ring)) return undefined;
    return ring.map(([lng, lat]) => ({ lat, lng }));
  }, [pendingRequestArea]);

  const existingRequestPath = useMemo((): { lat: number; lng: number }[] | undefined => {
    const area = initialRequestArea ?? null;
    if (!area) return undefined;
    if (area.type === 'Circle') {
      const ring = circleToGeoJSONRing(area.center[1], area.center[0], area.radius);
      return ring.map(([lng, lat]) => ({ lat, lng }));
    }
    return geofenceToPath(area as GeofenceGeoJSON);
  }, [initialRequestArea]);

  const schoolGeofencePath = useMemo(
    () => geofenceToPath(schoolGeofence ?? null),
    [schoolGeofence]
  );

  const onPolygonComplete = useCallback((polygon: google.maps.Polygon) => {
    if (existingRequestAreaPolygonRef.current) {
      existingRequestAreaPolygonRef.current.setMap(null);
      existingRequestAreaPolygonRef.current = null;
    }
    if (lastDrawnPolygonRef.current) {
      lastDrawnPolygonRef.current.setMap(null);
      lastDrawnPolygonRef.current = null;
    }
    const path = polygon.getPath();
    const arr = path.getArray();
    const ring = pathToGeoJSONRing(arr);
    if (ring.length >= 4) {
      setPendingRequestArea({ type: 'Polygon', coordinates: [ring] });
      lastDrawnPolygonRef.current = polygon;
    } else {
      polygon.setMap(null);
    }
  }, []);

  const onCircleComplete = useCallback((circle: google.maps.Circle) => {
    if (existingRequestAreaPolygonRef.current) {
      existingRequestAreaPolygonRef.current.setMap(null);
      existingRequestAreaPolygonRef.current = null;
    }
    if (lastDrawnPolygonRef.current) {
      lastDrawnPolygonRef.current.setMap(null);
      lastDrawnPolygonRef.current = null;
    }
    const center = circle.getCenter();
    const radius = circle.getRadius();
    if (center && radius != null && radius > 0) {
      const lat = center.lat();
      const lng = center.lng();
      setPendingRequestArea({ type: 'Circle', center: [lng, lat], radius });
    }
    circle.setMap(null);
  }, []);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      if (schoolGeofencePolygonRef.current) {
        schoolGeofencePolygonRef.current.setMap(null);
        schoolGeofencePolygonRef.current = null;
      }
      if (schoolGeofencePolylineRef.current) {
        schoolGeofencePolylineRef.current.setMap(null);
        schoolGeofencePolylineRef.current = null;
      }
      if (schoolGeofencePath && schoolGeofencePath.length >= 3) {
        const path = schoolGeofencePath.map((p) => new google.maps.LatLng(p.lat, p.lng));
        schoolGeofencePolygonRef.current = new google.maps.Polygon({
          paths: path,
          map,
          fillColor: '#1565c0',
          fillOpacity: 0.15,
          strokeColor: '#1565c0',
          strokeWeight: 2,
          zIndex: 1,
        });
        schoolGeofencePolylineRef.current = new google.maps.Polyline({
          path,
          map,
          strokeColor: '#0d47a1',
          strokeWeight: 3,
          zIndex: 2,
        });
      }

      if (existingRequestAreaPolygonRef.current) {
        existingRequestAreaPolygonRef.current.setMap(null);
        existingRequestAreaPolygonRef.current = null;
      }
      if (existingRequestPath && existingRequestPath.length >= 3) {
        const path = existingRequestPath.map((p) => new google.maps.LatLng(p.lat, p.lng));
        existingRequestAreaPolygonRef.current = new google.maps.Polygon({
          paths: path,
          map,
          fillColor: '#ff9800',
          fillOpacity: 0.35,
          strokeColor: '#e65100',
          strokeWeight: 3,
          zIndex: 5,
        });
      }

      if (typeof google.maps.drawing === 'undefined') return;
      const manager = new google.maps.drawing.DrawingManager({
        map,
        drawingControl: true,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [
            google.maps.drawing.OverlayType.POLYGON,
            google.maps.drawing.OverlayType.CIRCLE,
          ],
        },
        polygonOptions: {
          fillColor: '#ff9800',
          fillOpacity: 0.25,
          strokeColor: '#e65100',
          strokeWeight: 2,
          editable: true,
        },
        circleOptions: {
          fillColor: '#ff9800',
          fillOpacity: 0.25,
          strokeColor: '#e65100',
          strokeWeight: 2,
          editable: true,
        },
      });
      google.maps.event.addListener(manager, 'polygoncomplete', onPolygonComplete);
      google.maps.event.addListener(manager, 'circlecomplete', onCircleComplete);
      drawingManagerRef.current = manager;
      setDrawingManagerReady(true);
    },
    [existingRequestPath, schoolGeofencePath, onPolygonComplete, onCircleComplete]
  );

  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof google === 'undefined') return;

    if (schoolGeofencePolygonRef.current) {
      schoolGeofencePolygonRef.current.setMap(null);
      schoolGeofencePolygonRef.current = null;
    }
    if (schoolGeofencePolylineRef.current) {
      schoolGeofencePolylineRef.current.setMap(null);
      schoolGeofencePolylineRef.current = null;
    }
    if (schoolGeofencePath && schoolGeofencePath.length >= 3) {
      const path = schoolGeofencePath.map((p) => new google.maps.LatLng(p.lat, p.lng));
      schoolGeofencePolygonRef.current = new google.maps.Polygon({
        paths: path,
        map,
        fillColor: '#1565c0',
        fillOpacity: 0.15,
        strokeColor: '#1565c0',
        strokeWeight: 2,
        zIndex: 1,
      });
      schoolGeofencePolylineRef.current = new google.maps.Polyline({
        path,
        map,
        strokeColor: '#0d47a1',
        strokeWeight: 3,
        zIndex: 2,
      });
    }
  }, [schoolGeofencePath]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || typeof google === 'undefined') return;

    if (existingRequestAreaPolygonRef.current) {
      existingRequestAreaPolygonRef.current.setMap(null);
      existingRequestAreaPolygonRef.current = null;
    }
    if (existingRequestPath && existingRequestPath.length >= 3) {
      const path = existingRequestPath.map((p) => new google.maps.LatLng(p.lat, p.lng));
      existingRequestAreaPolygonRef.current = new google.maps.Polygon({
        paths: path,
        map,
        fillColor: '#ff9800',
        fillOpacity: 0.35,
        strokeColor: '#e65100',
        strokeWeight: 3,
        zIndex: 5,
      });
    }
  }, [existingRequestPath]);

  useEffect(() => {
    return () => {
      if (schoolGeofencePolygonRef.current) {
        schoolGeofencePolygonRef.current.setMap(null);
        schoolGeofencePolygonRef.current = null;
      }
      if (schoolGeofencePolylineRef.current) {
        schoolGeofencePolylineRef.current.setMap(null);
        schoolGeofencePolylineRef.current = null;
      }
      if (existingRequestAreaPolygonRef.current) {
        existingRequestAreaPolygonRef.current.setMap(null);
        existingRequestAreaPolygonRef.current = null;
      }
      if (lastDrawnPolygonRef.current) {
        lastDrawnPolygonRef.current.setMap(null);
        lastDrawnPolygonRef.current = null;
      }
      if (drawingManagerRef.current) {
        drawingManagerRef.current.setMap(null);
        drawingManagerRef.current = null;
      }
    };
  }, []);

  const startDrawingPolygon = useCallback(() => {
    const manager = drawingManagerRef.current;
    if (manager && typeof google !== 'undefined' && google.maps?.drawing?.OverlayType?.POLYGON != null) {
      manager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
    }
  }, []);

  const startDrawingCircle = useCallback(() => {
    const manager = drawingManagerRef.current;
    if (manager && typeof google !== 'undefined' && google.maps?.drawing?.OverlayType?.CIRCLE != null) {
      manager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE);
    }
  }, []);

  const clearRequestArea = useCallback(() => {
    if (existingRequestAreaPolygonRef.current) {
      existingRequestAreaPolygonRef.current.setMap(null);
      existingRequestAreaPolygonRef.current = null;
    }
    if (lastDrawnPolygonRef.current) {
      lastDrawnPolygonRef.current.setMap(null);
      lastDrawnPolygonRef.current = null;
    }
    setPendingRequestArea(null);
  }, []);

  const handleSave = useCallback(() => {
    if (lastDrawnPolygonRef.current) {
      lastDrawnPolygonRef.current.setMap(null);
      lastDrawnPolygonRef.current = null;
    }
    const area = hasRequestArea(pendingRequestArea) ? pendingRequestArea : (hasRequestArea(initialRequestArea ?? null) ? initialRequestArea! : null);
    const center = getCenterFromRequestArea(area) || schoolCenter;
    onSave({
      latitude: center?.lat ?? DEFAULT_CENTER.lat,
      longitude: center?.lng ?? DEFAULT_CENTER.lng,
      requestArea: area,
    });
  }, [pendingRequestArea, initialRequestArea, schoolCenter, onSave]);

  if (!apiKey) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Google Maps API key is not set.</Typography>
        <Button onClick={onCancel} sx={{ mt: 2 }}>Close</Button>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Failed to load Google Maps: {loadError.message}</Typography>
        <Button onClick={onCancel} sx={{ mt: 2 }}>Close</Button>
      </Box>
    );
  }

  const hasSchoolGeofence = !!(schoolGeofencePath && schoolGeofencePath.length >= 3);
  const mapContainerStyle = fullScreen ? { width: '100%', height: '100%' } : MAP_CONTAINER_STYLE;

  return (
    <Box sx={fullScreen ? { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 } : undefined}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Request area:</strong> Draw a polygon or circle that parents must be inside to request dispersal at this gate. The school boundary is shown so you can place the gate area inside or outside it.
      </Typography>
      {(hasSchoolGeofence || hasRequestArea(pendingRequestArea)) && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          {hasSchoolGeofence && (
            <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 16, height: 12, border: '2px solid #1565c0', borderRadius: 0.5, bgcolor: 'rgba(21,101,192,0.2)' }} />
              School boundary
            </Typography>
          )}
          {hasRequestArea(pendingRequestArea) && (
            <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 16, height: 12, bgcolor: 'rgba(255,152,0,0.4)', border: '2px solid #e65100', borderRadius: 0.5 }} />
              Gate request area
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button variant="outlined" size="small" onClick={startDrawingPolygon} disabled={!scriptLoaded || !drawingManagerReady}>
          Draw polygon
        </Button>
        <Button variant="outlined" size="small" onClick={startDrawingCircle} disabled={!scriptLoaded || !drawingManagerReady}>
          Draw circle
        </Button>
        {hasRequestArea(pendingRequestArea) ? (
          <Button variant="outlined" size="small" color="warning" onClick={clearRequestArea}>
            Clear request area
          </Button>
        ) : null}
      </Box>
      {!scriptLoaded ? (
        <Box sx={fullScreen ? { flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } : { height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading map…</Box>
      ) : (
        <Box sx={fullScreen ? { flex: 1, minHeight: 0 } : undefined}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          options={{ mapTypeControl: true, fullscreenControl: true, zoomControl: true }}
        >
          {pendingRequestPath && pendingRequestPath.length >= 3 && (
            <Polygon
              paths={pendingRequestPath}
              options={{
                fillColor: '#ff9800',
                fillOpacity: 0.35,
                strokeColor: '#e65100',
                strokeWeight: 3,
                zIndex: 2,
              }}
            />
          )}
        </GoogleMap>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2, flexShrink: 0 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save request area
        </Button>
      </Box>
    </Box>
  );
};

export default GateMapEditor;
